import React, { useState } from 'react';
import { FloppyDisk, Gear } from '@phosphor-icons/react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../ui/dialog';
import { BlockEditor } from './BlockEditor';

// The date input works in plain calendar days; the stored value keeps its original time of day
// so re-saving a post never silently shifts it.
const toDateInput = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');

const fromDateInput = (day, previousIso) => {
  if (!day) return null;
  if (previousIso && toDateInput(previousIso) === day) return previousIso;
  return new Date(`${day}T12:00:00.000Z`).toISOString();
};

const slugify = (value) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

export const PostEditorDialog = ({
  open,
  onOpenChange,
  post,
  categories,
  onSave,
  onManageCategories,
}) => {
  const [form, setForm] = useState({
    title: post?.title || '',
    slug: post?.slug || '',
    category: post?.category || categories[0]?.slug || '',
    excerpt: post?.excerpt || '',
    cover_image: post?.cover_image || '',
    blocks: post?.blocks || [],
    published: post?.published || false,
    is_featured: post?.is_featured || false,
    published_at: post?.published_at || null,
  });
  const [publishDay, setPublishDay] = useState(toDateInput(post?.published_at));
  const [saving, setSaving] = useState(false);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        ...form,
        slug: form.slug || slugify(form.title),
        published_at: fromDateInput(publishDay, post?.published_at),
      });
      onOpenChange(false);
    } catch (error) {
      /* surfaced by caller */
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="post-editor-dialog">
        <DialogHeader>
          <DialogTitle>{post ? 'Edit post' : 'New post'}</DialogTitle>
          <DialogDescription>
            Build the body from blocks — text, highlights, images and references, in any order.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <Label htmlFor="post-title">Title</Label>
            <Input
              id="post-title"
              value={form.title}
              onChange={(e) =>
                set({ title: e.target.value, slug: post ? form.slug : slugify(e.target.value) })
              }
              placeholder="Castica 9 is fully funded"
              required
              className="mt-1.5"
              data-testid="post-title-input"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="post-slug">URL</Label>
              <Input
                id="post-slug"
                value={form.slug}
                onChange={(e) => set({ slug: e.target.value })}
                placeholder="castica-9-fully-funded"
                required
                className="mt-1.5"
                data-testid="post-slug-input"
              />
              <p className="text-xs text-foreground-muted mt-1">/news/{form.slug || '...'}</p>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="post-category">Category</Label>
                <button
                  type="button"
                  onClick={onManageCategories}
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  data-testid="manage-categories-link"
                >
                  <Gear size={13} /> Manage
                </button>
              </div>
              <select
                id="post-category"
                value={form.category}
                onChange={(e) => set({ category: e.target.value })}
                className="mt-1.5 w-full h-10 px-3 rounded-lg border border-black/10 bg-white text-sm focus:border-primary focus:ring-1 focus:ring-primary"
                required
                data-testid="post-category-select"
              >
                {categories.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <Label htmlFor="post-excerpt">Excerpt</Label>
            <textarea
              id="post-excerpt"
              value={form.excerpt}
              onChange={(e) => set({ excerpt: e.target.value })}
              rows={2}
              placeholder="One or two lines shown on the card and under the headline."
              required
              className="mt-1.5 w-full px-3 py-2 rounded-lg border border-black/10 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
              data-testid="post-excerpt-input"
            />
          </div>

          <div>
            <Label htmlFor="post-cover">Cover image URL</Label>
            <Input
              id="post-cover"
              value={form.cover_image}
              onChange={(e) => set({ cover_image: e.target.value })}
              placeholder="https://..."
              className="mt-1.5"
              data-testid="post-cover-input"
            />
            {form.cover_image && (
              <div className="mt-2 rounded-xl overflow-hidden border border-black/[0.06] max-h-40">
                <img src={form.cover_image} alt="" className="w-full object-cover" />
              </div>
            )}
          </div>

          <div className="pt-2 border-t border-black/[0.06]">
            <Label className="mb-3 block">Body</Label>
            <BlockEditor blocks={form.blocks} onChange={(blocks) => set({ blocks })} />
          </div>

          <div className="pt-2 border-t border-black/[0.06]">
            <Label htmlFor="post-date">Publication date</Label>
            <Input
              id="post-date"
              type="date"
              value={publishDay}
              onChange={(e) => setPublishDay(e.target.value)}
              className="mt-1.5 max-w-[220px]"
              data-testid="post-date-input"
            />
            <p className="text-xs text-foreground-muted mt-1">
              {publishDay
                ? 'Shown on the card and under the headline.'
                : 'Leave empty to stamp today when you publish.'}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-6 pt-2 border-t border-black/[0.06]">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.published}
                onChange={(e) => set({ published: e.target.checked })}
                className="w-4 h-4 rounded border-black/20 text-primary focus:ring-primary"
                data-testid="post-published-checkbox"
              />
              <span className="text-sm font-medium">Published</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_featured}
                onChange={(e) => set({ is_featured: e.target.checked })}
                className="w-4 h-4 rounded border-black/20 text-primary focus:ring-primary"
                data-testid="post-featured-checkbox"
              />
              <span className="text-sm font-medium">Feature at the top</span>
            </label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="post-cancel-button">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-primary hover:bg-primary-hover"
              data-testid="post-save-button"
            >
              <FloppyDisk size={17} className="mr-2" />
              {saving ? 'Saving...' : 'Save post'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
